import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, ArrowLeft, RefreshCw, X, Bot, Activity, CirclePause, AlertTriangle, Layers, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ThemeToggle } from '@/components/ThemeToggle'
import { AgentListTable } from '@/components/AgentListTable'
import { AgentRegistrationSlider } from '@/components/AgentRegistrationSlider'
import { AgentDetailsSlider } from '@/components/AgentDetailsSlider'
import { useAgentRegistry } from '@/hooks/useAgentRegistry'
import { useAuth } from '@/hooks/useAuth'
import { fetchWithAuth } from '@/lib/fetchWithAuth'
import { cn } from '@/lib/utils'
import type { AgentResponse, AgentDetailResponse, AgentStatus, AgentRegistrationRequest, AgentUpdateRequest } from '@/types/api'

const STATUS_FILTERS: { label: string; value: AgentStatus | undefined }[] = [
  { label: 'All', value: undefined },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Pending', value: 'pending_validation' },
  { label: 'Failed', value: 'failed_validation' },
]

export function AgentManagement() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const {
    agents,
    loading,
    error,
    listAgents,
    getAgent,
    registerAgent,
    updateAgent,
    deleteAgent,
    validateAgent,
    healthCheckAgent,
    activateAgent,
    deactivateAgent,
    checkBaseUrl,
    getDbConnection,
    fetchAgentInfo,
    testConnection,
    testConnectionAdhoc,
    introspectSchema,
    enrichSchema,
  } = useAgentRegistry()

  const [selectedAgent, setSelectedAgent] = useState<AgentDetailResponse | null>(null)
  const [detailsSliderOpen, setDetailsSliderOpen] = useState(false)
  const [registrationSliderOpen, setRegistrationSliderOpen] = useState(false)
  const [registrationMode, setRegistrationMode] = useState<'create' | 'edit'>('create')
  const [editingAgent, setEditingAgent] = useState<AgentResponse | null>(null)
  const [statusFilter, setStatusFilter] = useState<AgentStatus | undefined>(undefined)
  const [showTools, setShowTools] = useState(false)
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // Compute agent stats
  const stats = useMemo(() => {
    const total = agents.length
    const active = agents.filter((a) => a.status === 'active').length
    const inactive = agents.filter((a) => a.status === 'inactive').length
    const failed = agents.filter((a) => a.status === 'failed_validation').length
    return { total, active, inactive, failed }
  }, [agents])

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size)
  }, [])

  useEffect(() => {
    listAgents(statusFilter).catch(() => {
      // Error handled by hook
    })
  }, [statusFilter])

  // Filter agents based on showTools toggle — SQL agents are always visible (they are data sources,
  // not internal tools). Only tool-type agents are hidden when the toggle is off.
  const filteredAgents = useMemo(
    () =>
      showTools
        ? agents
        : agents.filter((a) => a.agent_type !== 'tool'),
    [agents, showTools]
  )

  const totalCount = filteredAgents.length

  const maxPage = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize) || 1),
    [totalCount, pageSize],
  )
  const currentPage = Math.min(Math.max(1, page), maxPage)
  const pagedAgents = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredAgents.slice(start, start + pageSize)
  }, [filteredAgents, currentPage, pageSize])

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), type === 'info' ? 10000 : 5000)
  }

  const handleViewDetails = async (agent: AgentResponse) => {
    try {
      const details = await getAgent(agent.agent_id)
      setSelectedAgent(details)
      setDetailsSliderOpen(true)
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to load agent details')
    }
  }

  const handleEdit = (agent: AgentResponse) => {
    setEditingAgent(agent)
    setRegistrationMode('edit')
    setRegistrationSliderOpen(true)
  }

  const handleRegister = () => {
    setEditingAgent(null)
    setRegistrationMode('create')
    setRegistrationSliderOpen(true)
  }

  const handleSubmitRegistration = async (data: AgentRegistrationRequest | AgentUpdateRequest) => {
    try {
      if (registrationMode === 'create') {
        const result = await registerAgent(data as AgentRegistrationRequest)
        const isSql = (data as AgentRegistrationRequest).agent_type === 'sql'
        if (isSql) {
          showNotification(
            'info',
            'Agent registered. Generating AI descriptions and accumulating data from your database. We will notify you once your agent is ready.'
          )
          // Start SSE listener for background setup progress
          monitorSetupProgress(result.agent_id)
        } else {
          showNotification('success', 'Agent registered successfully')
        }
      } else if (editingAgent) {
        await updateAgent(editingAgent.agent_id, data as AgentUpdateRequest)
        showNotification('success', 'Agent updated successfully')
      }
      setRegistrationSliderOpen(false)
      setEditingAgent(null)
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to save agent')
      throw err
    }
  }

  const monitorSetupProgress = async (agentId: string) => {
    try {
      const response = await fetchWithAuth(
        `/api/agents/${encodeURIComponent(agentId)}/setup-progress`,
        { headers: { Accept: 'text/event-stream' } }
      )
      if (!response.ok || !response.body) return

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Parse SSE lines
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        let currentEvent = ''
        for (const line of lines) {
          if (line.startsWith('event: ')) currentEvent = line.slice(7).trim()
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6))
            if (currentEvent === 'setup_complete') {
              showNotification('success', `Agent "${agentId}" is ready! Schema and AI descriptions generated.`)
              listAgents()
              return
            }
            if (currentEvent === 'setup_failed') {
              showNotification('error', `Agent "${agentId}" setup failed: ${data.error || 'Unknown error'}`)
              listAgents()
              return
            }
          }
        }
      }
    } catch {
      // Silently ignore — agent may still succeed, user can refresh
    }
  }

  const handleDelete = async (agent: AgentResponse) => {
    if (!confirm(`Are you sure you want to delete agent "${agent.name}"? This action cannot be undone.`)) {
      return
    }

    try {
      await deleteAgent(agent.agent_id)
      showNotification('success', 'Agent deleted successfully')
      if (detailsSliderOpen && selectedAgent?.agent_id === agent.agent_id) {
        setDetailsSliderOpen(false)
        setSelectedAgent(null)
      }
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to delete agent')
    }
  }

  const handleActivate = async (agent: AgentResponse) => {
    try {
      await activateAgent(agent.agent_id)
      showNotification('success', 'Agent activated successfully')
      if (detailsSliderOpen && selectedAgent?.agent_id === agent.agent_id) {
        const updated = await getAgent(agent.agent_id)
        setSelectedAgent(updated)
      }
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to activate agent')
    }
  }

  const handleDeactivate = async (agent: AgentResponse) => {
    try {
      await deactivateAgent(agent.agent_id)
      showNotification('success', 'Agent deactivated successfully')
      if (detailsSliderOpen && selectedAgent?.agent_id === agent.agent_id) {
        const updated = await getAgent(agent.agent_id)
        setSelectedAgent(updated)
      }
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to deactivate agent')
    }
  }

  const handleValidate = async (agent: AgentResponse) => {
    try {
      await validateAgent(agent.agent_id)
      showNotification('success', 'Agent validation completed')
      if (detailsSliderOpen && selectedAgent?.agent_id === agent.agent_id) {
        const updated = await getAgent(agent.agent_id)
        setSelectedAgent(updated)
      }
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to validate agent')
    }
  }

  const handleHealthCheck = async (agent: AgentResponse) => {
    try {
      const isHealthy = await healthCheckAgent(agent.agent_id)
      showNotification(
        isHealthy ? 'success' : 'error',
        isHealthy ? 'Agent health check passed' : 'Agent health check failed'
      )
      if (detailsSliderOpen && selectedAgent?.agent_id === agent.agent_id) {
        const updated = await getAgent(agent.agent_id)
        setSelectedAgent(updated)
      }
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to perform health check')
    }
  }

  const handleRefresh = async () => {
    try {
      await listAgents(statusFilter)
      showNotification('success', 'Agent list refreshed')
    } catch {
      showNotification('error', 'Failed to refresh agent list')
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ─── Fixed Top Nav Bar ─── */}
      <header className="h-12 border-b border-border bg-card/80 backdrop-blur-sm flex-shrink-0 sticky top-0 z-40">
        <div className="h-full px-4 flex items-center justify-between max-w-7xl mx-auto">
          {/* Left: Back + Brand + Page title */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="h-8 w-8 rounded-full"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-semibold text-foreground text-sm hidden sm:block">Universal AI Platform</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-border mx-1" />
            <span className="hidden sm:block text-sm text-muted-foreground">Agent Management</span>
          </div>

          {/* Right: Actions + Profile + Logout */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="h-8 px-2"
            >
              <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            </Button>
            <ThemeToggle />
            {user && (
              <div className="flex items-center gap-1.5 ml-1">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                  {user.username.charAt(0).toUpperCase()}
                </div>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="h-8 px-2">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Toast notification */}
      {notification && (
        <div className="fixed top-14 right-4 z-50 animate-fade-in-up">
          <div
            className={cn(
              'flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium max-w-sm',
              notification.type === 'success'
                ? 'bg-card border-primary/30 text-foreground'
                : notification.type === 'info'
                ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400'
                : 'bg-destructive/10 border-destructive/30 text-destructive'
            )}
          >
            <span className="flex-1">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ─── Main Content ─── */}
      <div className="flex-1 flex flex-col min-h-0 max-w-7xl mx-auto w-full px-6 py-4 gap-4">
        {/* ─── Stats + Register row ─── */}
        <div className="flex items-stretch gap-3">
          {/* Stats cards */}
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
                <Layers className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <span className="text-lg font-bold text-foreground leading-none">{stats.total}</span>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Total</p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                <Activity className="h-4 w-4 text-success" />
              </div>
              <div>
                <span className="text-lg font-bold text-foreground leading-none">{stats.active}</span>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Active</p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
                <CirclePause className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <span className="text-lg font-bold text-foreground leading-none">{stats.inactive}</span>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Inactive</p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </div>
              <div>
                <span className="text-lg font-bold text-foreground leading-none">{stats.failed}</span>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Failed</p>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Filter + Table Section ─── */}
        <div className="rounded-xl border border-border bg-card flex flex-col flex-1 min-h-0 overflow-visible">
          {/* Filter bar */}
          <div className="px-4 py-2.5 border-b border-border bg-muted/30 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {STATUS_FILTERS.map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => {
                    setStatusFilter(value)
                    setPage(1)
                  }}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200',
                    statusFilter === value
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setShowTools((v) => !v); setPage(1) }}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all border',
                  showTools
                    ? 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800'
                    : 'text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                )}
              >
                {showTools ? 'Hide Tools' : 'Show Tools'}
              </button>
              <span className="text-xs text-muted-foreground">
                {filteredAgents.length} agent{filteredAgents.length !== 1 ? 's' : ''}
              </span>
              <Button onClick={handleRegister} size="sm" className="h-8 text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Register Agent
              </Button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="px-4 pt-3">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}

          {/* Agent Table */}
          <AgentListTable
            agents={pagedAgents}
            totalCount={totalCount}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            onViewDetails={handleViewDetails}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onValidate={handleValidate}
            onHealthCheck={handleHealthCheck}
          />
        </div>
      </div>

      {/* Registration Slider */}
      <AgentRegistrationSlider
        open={registrationSliderOpen}
        onOpenChange={setRegistrationSliderOpen}
        onSubmit={handleSubmitRegistration}
        agent={editingAgent}
        mode={registrationMode}
        checkBaseUrl={checkBaseUrl}
        getDbConnection={getDbConnection}
        fetchAgentInfo={fetchAgentInfo}
        testConnection={testConnection}
        testConnectionAdhoc={testConnectionAdhoc}
      />

      {/* Details Slider */}
      <AgentDetailsSlider
        open={detailsSliderOpen}
        onOpenChange={setDetailsSliderOpen}
        agent={selectedAgent}
        testConnection={testConnection}
        onEdit={() => {
          if (selectedAgent) {
            setEditingAgent(selectedAgent)
            setRegistrationMode('edit')
            setDetailsSliderOpen(false)
            setRegistrationSliderOpen(true)
          }
        }}
        onDelete={() => {
          if (selectedAgent) {
            handleDelete(selectedAgent)
          }
        }}
        onActivate={() => {
          if (selectedAgent) {
            handleActivate(selectedAgent)
          }
        }}
        onDeactivate={() => {
          if (selectedAgent) {
            handleDeactivate(selectedAgent)
          }
        }}
        onValidate={() => {
          if (selectedAgent) {
            handleValidate(selectedAgent)
          }
        }}
        onHealthCheck={() => {
          if (selectedAgent) {
            handleHealthCheck(selectedAgent)
          }
        }}
        introspectSchema={introspectSchema}
        enrichSchema={enrichSchema}
        fetchAgentInfo={fetchAgentInfo}
        onRefresh={async () => {
          if (selectedAgent) {
            try {
              const updated = await getAgent(selectedAgent.agent_id)
              setSelectedAgent(updated)
            } catch { /* ignore */ }
          }
        }}
      />
    </div>
  )
}
